import * as React from 'react';
import { useEffect } from 'react';
import { ICardDescriptionProps } from './types';
import './_CardDescription.scss';
import { Button, ButtonsGroup, ButtonTitle, Icon, Input } from 'kui';
import {
    CARD_DESCRIPTION_CANCEL,
    CARD_DESCRIPTION_SAVE,
    CARD_DESCRIPTION_UNSAVED,
    CARD_DESCRIPTION_UNSAVED_DELETE_TOOLTIP,
    ENTER_WHICH_KEY_NUMBER,
} from './constants';
import { CLASS_CARD_DETAILS } from '../../../constants';
import { getAsidePanelTooltip } from 'app/view/react_components/aside_panel/asidePanel/helpers/getAsidePanelTooltip';
import { blockMarkdown } from 'app/helper/markdownHelper';
import { HtmlEditorHOC } from '../../hocs/HtmlEditorHOC/HtmlEditorHOC';
import { AsidePanelContext } from 'app/view/react_components/aside_panel/asidePanel/components/AsidePanel/constants';
import { EHTMLEditorType } from 'app/types/types';
import { CardDescriptionRender } from './CardDescriptionRender';
import { isNull } from 'underscore';
import { msFileThumbnailStorage } from '../../../../../../../helper/authorisation/microsoft/storage/msThumbnail';

export function CardDescription({
    description,
    descriptionUnsaved,
    HTMLEditorType,
    isPinned,
    isReadonly,
    isRequired,
    onCancel,
    onCancelWithoutChanges,
    onEsc,
    onSave,
    onCtrlEnter,
    onSaveUnsavedDescription,
    onDeleteUnsavedDescription,
    onShowBoardLimitSnackbarIfNeeded,
    onShowUnsavedDescription,
    onTogglePin
}: ICardDescriptionProps) {
    const { cardId, isMobile } = React.useContext(AsidePanelContext);

    const className = CLASS_CARD_DETAILS + '__description';
    const classNameInput = CLASS_CARD_DETAILS + '__description-input';
    const classNameRequired = className + '--required';
    const classNameButtonContainer = CLASS_CARD_DETAILS + '__description-button-container';
    const classNameUnsavedEditsButtonContainer = CLASS_CARD_DETAILS + '__description-unsaved-edits-button-container';
    const classNameUnsavedEditsButtonTitle = CLASS_CARD_DETAILS + '__description-unsaved-edits-button-title';
    const classNameButtonSave = CLASS_CARD_DETAILS + '__description-button-save';
    const classNameError = className + '--error';

    const [markdown, setMarkdown] = React.useState('');
    const [isError, setError] = React.useState(false);
    const [lastState] = React.useState<any>({});
    lastState.saveUnsavedDescription = saveUnsavedDescription;
    const [isInput, setIsInput] = React.useState(false);
    const [isCollapse, setCollapse] = React.useState(true);
    const [needCollapse, setNeedCollapse] = React.useState(false);
    const [selectionTree, setSelectionTree] = React.useState([]);
    const [isShowUnsavedData, setIsShowUnsavedData] = React.useState(true);

    const containerRef = React.useRef(null);
    const inputRef = React.useRef(null);
    const descriptionEditor = React.useRef(null);
    const controlsRef = React.useRef(null);

    function handleHtmlChange(value: string) {
        setTimeout(() => { // fix: на umount quill почему-то возвращает исходное value
            if (isInput) {
                descriptionEditor.current = value;
            }
        }, 100);
    }

    function handleCtrlEnter(event?: React.KeyboardEvent) {
        if (event) event.preventDefault();
        setIsInput(false);
        setErrorIfEmpty();
        onCtrlEnter(descriptionEditor.current);
    }

    useEffect(() => {
        descriptionEditor.current = description;
    }, [description]);

    const setErrorIfEmpty = () => {
        if (isRequired && !descriptionEditor.current) {
            setError(true);
        }
    }

    function onInputKeyDown(event: React.KeyboardEvent) {
        if (event.key === 'Escape') {
            event.stopPropagation();//to avoid aside-panel closure on esc if description is in focus
            descriptionEditor.current = description;
            setIsInput(false);
            setErrorIfEmpty();
            onEsc();
        } else if (event.which === ENTER_WHICH_KEY_NUMBER && (event.ctrlKey || event.metaKey)) {
            handleCtrlEnter(event);
        } else if (event.key === 'Enter') {
            event.preventDefault();
            const textarea = containerRef.current.querySelector('textarea');
            if (textarea) {
                const startPos = textarea.selectionStart;
                const endPos = textarea.selectionEnd;
                const value = textarea.value;
                textarea.value = value.substring(0, startPos) + '\n' + value.substring(endPos);
                textarea.selectionStart = textarea.selectionEnd = startPos + 1;
            }
        }
    }

    function handlerSaveClicked(e: React.SyntheticEvent) {
        setIsInput(false);
        setErrorIfEmpty();
        onSave(descriptionEditor.current);
        setMarkdown(blockMarkdown(description));
    }

    function handleCancelClicked() {
        if (descriptionEditor.current === description) {
            onCancelWithoutChanges();
        } else {
            onCancel();
            descriptionEditor.current = description;
            setIsShowUnsavedData(true)
        }
        setIsInput(false);
        setErrorIfEmpty();
        setMarkdown(blockMarkdown(description));
    }

    function saveUnsavedDescription() {
        setIsInput(false);
        if (descriptionEditor.current !== description) {
            onSaveUnsavedDescription(descriptionEditor.current);
        }
    }

    function handleInputChange(e: React.SyntheticEvent) {
        let { value } = e.target as HTMLInputElement;
        descriptionEditor.current = value;
    }

    function handleUnsavedDataClicked() {
        setMarkdown(blockMarkdown(descriptionUnsaved.descriptionUnsaved));
        descriptionEditor.current = descriptionUnsaved.descriptionUnsaved;
        setIsInput(true);
        onShowUnsavedDescription();
        setIsShowUnsavedData(false)
    }
    function onCloseUnsavedDescription() {
        descriptionEditor.current = description;
        onDeleteUnsavedDescription();
    }

    React.useEffect(() => {
        if (
            isRequired &&
            descriptionEditor.current &&
            descriptionEditor.current.length
        ) {
            setError(false);
        }
    }, [descriptionEditor.current, isInput]);

    const [isControlsSticky, setControlsSticky] = React.useState(false);
    React.useEffect(() => {
        function createObserver() {
            if (!controlsRef.current) return;
            const observer = new IntersectionObserver(([e]) => {
                    setControlsSticky(e.intersectionRatio < 1);
                }, {
                    threshold: 1
                }
            );
            observer.observe(controlsRef.current);
        }
        requestAnimationFrame(createObserver); // когда элементы появятся в доме
    }, [isInput]);

    const [isCloneCollapse, setCloneCollapse] = React.useState(null);
    React.useEffect(() => {
        if (isNull(isCloneCollapse)) return;
        setCollapse(isCloneCollapse);
    }, [isCloneCollapse]);

    React.useEffect(() => {
        setMarkdown(blockMarkdown(description));

        if (description && msFileThumbnailStorage.is()) {
            msFileThumbnailStorage.getSrcThumbnails(description)
                .then((text) => {
                    setMarkdown(blockMarkdown(text));
                })
                .catch(() => {})
        }
    }, [description]); //, isInput

    const cardDescriptionUnsavedDate = !!descriptionUnsaved && isShowUnsavedData && (
        <div
            className={ classNameUnsavedEditsButtonContainer }
        >
            <Button
                variant={ 'icon-text' }
                onClick={ handleUnsavedDataClicked }
            >
                <Icon xlink={ 'info' }/>
                <ButtonTitle>
                    <span className={classNameUnsavedEditsButtonTitle}>
                        {CARD_DESCRIPTION_UNSAVED}
                    </span>
                    { descriptionUnsaved.datetime }
                </ButtonTitle>
            </Button>
            <Button
                variant={ 'icon' }
                tooltip={ getAsidePanelTooltip({
                        direction: 'up-left',
                        value: CARD_DESCRIPTION_UNSAVED_DELETE_TOOLTIP
                    })
                }
                onClick={ onCloseUnsavedDescription }
            >
                <Icon xlink={ 'close' } size={24}/>
            </Button>
        </div>
    );

    return (
        <div className={`
                ${className}
                ${isRequired ? classNameRequired : ''}
                ${isError && !isInput ? classNameError : ''}
                ${isInput ? className + '--editing' : ''}
            `}
            ref={containerRef}
             onClick={onShowBoardLimitSnackbarIfNeeded}
        >
            {isInput && <>
                    {!0 || HTMLEditorType === EHTMLEditorType.MARKDOWN
                        ? <Input
                            autoFocus
                            className={classNameInput}
                            ref={ inputRef }
                            value={ descriptionEditor.current }
                            onKeyDown={ onInputKeyDown }
                            onChange={ handleInputChange }
                        />
                        : <HtmlEditorHOC
                            cardId={cardId}
                            selectionTree={selectionTree}
                            value={markdown}
                            onChange={handleHtmlChange}
                            onCtrlEnter={handleCtrlEnter}
                            onEscape={handleCancelClicked}
                        />
                    }
                    <div className={`${classNameButtonContainer} ${isControlsSticky ? 'sticky' : ''}`}>
                        <ButtonsGroup>
                            <Button
                                variant={'secondary'}
                                text={ CARD_DESCRIPTION_CANCEL }
                                onClick={ handleCancelClicked }
                            />
                            <Button
                                className={classNameButtonSave}
                                text={ CARD_DESCRIPTION_SAVE }
                                onClick={ handlerSaveClicked }
                            />
                        </ButtonsGroup>
                    </div>
                    <div ref={controlsRef} />
                </>
            }
            <>
                <CardDescriptionRender
                    description={description}
                    markdown={markdown}
                    descriptionEditor={descriptionEditor}
                    isCollapse={isCollapse}
                    isInput={isInput}
                    isPinned={isPinned}
                    isReadonly={isReadonly}
                    needCollapse={needCollapse}
                    onSaveUnsavedDescription={onSaveUnsavedDescription}
                    onTogglePin={onTogglePin}
                    setSelectionTree={setSelectionTree}
                    setCollapse={(value) => {
                        setCollapse(value);
                        setCloneCollapse(value);
                    }}
                    setErrorIfEmpty={setErrorIfEmpty}
                    setIsInput={setIsInput}
                    setNeedCollapse={setNeedCollapse}
                />
                <CardDescriptionRender
                    description={description}
                    markdown={markdown}
                    descriptionEditor={descriptionEditor}
                    isClone
                    isCollapse={isCloneCollapse}
                    isPinned={isPinned}
                    isInput={isInput}
                    isReadonly={isReadonly}
                    needCollapse={needCollapse}
                    onSaveUnsavedDescription={onSaveUnsavedDescription}
                    onTogglePin={onTogglePin}
                    setSelectionTree={setSelectionTree}
                    setCollapse={setCloneCollapse}
                    setErrorIfEmpty={setErrorIfEmpty}
                    setIsInput={setIsInput}
                    setNeedCollapse={setNeedCollapse}
                />
                {cardDescriptionUnsavedDate}
            </>
        </div>
    );
}
